import React, { useState } from "react";
import { FaArrowRight, FaMapMarkerAlt, FaClock, FaBriefcase } from "react-icons/fa";
import { HiOutlineSearch } from "react-icons/hi";
import { MdOutlineWorkOutline } from "react-icons/md";
import HeadingBanner from "../../components/common/HeadingBanner";
import EmailBanner from "../../components/home/EmailBanner";
import { useNavigate } from "react-router-dom";

/* ─── Brand Tokens ─────────────────────────────────────────────────── */
const O  = "#F5A623";   // orange
const B  = "#7B96BD";   // steel blue
const BD = "#4d6d92";   // blue dark
const BL = "#eef4fb";   // blue light

/* ─── Data ─────────────────────────────────────────────────────────── */
const perks = [
  { emoji: "🚀", title: "Growth First",     desc: "Structured learning paths, certifications, and mentorship from day one." },
  { emoji: "⚡", title: "Move Fast",         desc: "Flat hierarchy, quick decisions, and space to ship ideas that matter." },
  { emoji: "🌍", title: "Remote Friendly",  desc: "Flexible work options — work from where you do your best thinking." },
  { emoji: "🎯", title: "Real Ownership",   desc: "You own your work end-to-end. No micro-management, just clear goals." },
  { emoji: "🤝", title: "Inclusive Team",   desc: "A diverse, collaborative culture where every voice drives our direction." },
  { emoji: "💡", title: "Meaningful Work",  desc: "Technology that solves real problems for institutions and communities." },
];

const jobs = [
  { id: 1, title: "Full Stack Developer",       dept: "Engineering",  type: "Full-time", location: "Remote / Atlanta", tags: ["React", "Node.js", "MongoDB"] },
  { id: 2, title: "UI/UX Designer",             dept: "Design",       type: "Full-time", location: "New Work",          tags: ["Figma", "Prototyping", "Research"] },
  { id: 3, title: "Business Development Exec.", dept: "Sales",        type: "Full-time", location: "Remote",                tags: ["B2B", "SaaS", "CRM"] },
  { id: 4, title: "Digital Marketing Manager",  dept: "Marketing",    type: "Full-time", location: "New Work",          tags: ["SEO", "Content", "Ads"] },
  { id: 5, title: "HR & Talent Acquisition",    dept: "HR",           type: "Full-time", location: "Atlanta",          tags: ["Recruiting", "HRMS", "Culture"] },
  { id: 6, title: "Software Engineer Intern",   dept: "Engineering",  type: "Internship",location: "Remote / Hybrid",      tags: ["JavaScript", "API", "Git"] },
  { id: 7, title: "Product Manager",            dept: "Product",      type: "Full-time", location: "Remote",                tags: ["Roadmap", "Agile", "B2B"] },
  { id: 8, title: "Sales Intern",               dept: "Sales",        type: "Internship",location: "Remote /Atlanta",          tags: ["Lead Gen", "Outreach", "Excel"] },
];

const depts = ["All", "Engineering", "Design", "Sales", "Marketing", "HR", "Product"];

const steps = [
  { n: "01", title: "Apply Online",      desc: "Submit your application through our careers portal. Takes less than 5 minutes." },
  { n: "02", title: "Screening Call",    desc: "A quick 20-min call with our HR team to understand your background and goals." },
  { n: "03", title: "Technical / Task",  desc: "A short role-specific assessment or technical round — no trick questions." },
  { n: "04", title: "Culture Interview", desc: "Meet your future team and learn how you'll collaborate and grow together." },
  { n: "05", title: "Offer & Onboarding",desc: "Fast decisions. Once you're in, our structured onboarding gets you ramped up quickly." },
];

/* ─── Small Components ─────────────────────────────────────────────── */
function Tag({ label }) {
  return (
    <span className="px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: BL, color: BD }}>
      {label}
    </span>
  );
}

function DeptBadge({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200"
      style={{
        background: active ? O : "white",
        color: active ? "white" : "#64748b",
        border: active ? `1.5px solid ${O}` : "1.5px solid #e2e8f0",
        boxShadow: active ? `0 4px 14px ${O}40` : "none",
      }}
    >
      {label}
    </button>
  );
}

/* ─── Main Page ────────────────────────────────────────────────────── */
export default function CareerPage() {
  const [activeDept, setActiveDept] = useState("All");
  const [search, setSearch]         = useState("");
  const [applied, setApplied]       = useState(null);
  const navigate = useNavigate();

  const filtered = jobs.filter((j) => {
    const matchDept = activeDept === "All" || j.dept === activeDept;
    const matchSearch = j.title.toLowerCase().includes(search.toLowerCase()) ||
      j.dept.toLowerCase().includes(search.toLowerCase());
    return matchDept && matchSearch;
  });

  return (
    <div className="bg-[#f8fafd] min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />
        <div>
            <HeadingBanner  title="Career" />
        </div>
    

      {/* ── PERKS ─────────────────────────────────────────────────────── */}
      <section className="py-4 lg:px-20 md:px-12 px-5 bg-white">
        <div className="">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: O }}>Why Techmentee</p>
            <h2 style={{ fontFamily: "'DM Serif Display', serif" }}
              className="text-4xl text-slate-900">
              A place to do your{" "}
              <em style={{ color: BD }}>best work</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {perks.map((p, i) => (
              <div key={i}
                className="group p-7 rounded-2xl border border-slate-100 hover:border-transparent transition-all duration-300 hover:-translate-y-1"
                style={{ "--hover-shadow": `0 8px 32px ${O}18` }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = `0 8px 32px ${O}18`}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              >
                <span className="text-3xl">{p.emoji}</span>
                <h3 className="mt-4 font-bold text-slate-800 text-base">{p.title}</h3>
                <p className="mt-2 text-slate-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OPEN ROLES ────────────────────────────────────────────────── */}
      <section id="openings" className="py-20 px-5">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: O }}>Open Positions</p>
              <h2 style={{ fontFamily: "'DM Serif Display', serif" }} className="text-4xl text-slate-900">
                Find your role
              </h2>
            </div>

            {/* Search */}
            <div className="relative">
              <HiOutlineSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search roles..."
                className="pl-11 pr-5 py-3 rounded-2xl border border-slate-200 bg-white text-sm text-slate-700 outline-none w-64 focus:border-orange-300 transition-colors"
              />
            </div>
          </div>

          {/* Dept filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {depts.map(d => (
              <DeptBadge key={d} label={d} active={activeDept === d} onClick={() => setActiveDept(d)} />
            ))}
          </div>

          {/* Job cards */}
          {filtered.length === 0 ? (
            <div className="py-20 text-center text-slate-400 text-sm">No roles match your search.</div>
          ) : (
            <div className="space-y-3">
              {filtered.map((job) => (
                <div key={job.id}
                  className="group flex flex-col sm:flex-row sm:items-center justify-between gap-5 bg-white p-6 rounded-2xl border border-slate-100 hover:border-transparent transition-all duration-200 hover:-translate-y-0.5"
                  onMouseEnter={e => e.currentTarget.style.boxShadow = `0 8px 28px ${B}22`}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                >
                  <div className="flex items-start gap-5">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
                      style={{ background: BL }}>
                      <FaBriefcase style={{ color: B }} className="text-base" />
                    </div>

                    <div>
                      <h3 className="font-bold text-slate-800 text-base group-hover:text-[#4d6d92] transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <FaMapMarkerAlt className="text-[10px]" /> {job.location}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <FaClock className="text-[10px]" /> {job.type}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-md font-medium"
                          style={{ background: job.type === "Internship" ? O + "18" : BL, color: job.type === "Internship" ? "#b8720a" : BD }}>
                          {job.dept}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {job.tags.map(t => <Tag key={t} label={t} />)}
                      </div>
                    </div>
                  </div>

                  {/* Apply button */}
                  {applied === job.id ? (
                    <div className="flex-shrink-0 px-6 py-2.5 rounded-xl text-sm font-semibold text-green-600 bg-green-50 border border-green-200">
                      ✓ Applied!
                    </div>
                  ) : (
                    <button
                      onClick={() => {setApplied(job.id) 
                        navigate("/contact-us")
                      }}
                      className="flex-shrink-0 flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:scale-105 active:scale-100"
                      style={{ background: `linear-gradient(135deg, ${O}, #e09010)`, boxShadow: `0 4px 14px ${O}40` }}>
                      Apply Now <FaArrowRight className="text-xs" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── PROCESS ───────────────────────────────────────────────────── */}
      <section id="process" className="py-20 px-5 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: O }}>How It Works</p>
            <h2 style={{ fontFamily: "'DM Serif Display', serif" }} className="text-4xl text-slate-900">
              Our hiring <em style={{ color: BD }}>process</em>
            </h2>
            <p className="mt-3 text-slate-400 text-sm max-w-lg mx-auto">
              Transparent, fast, and human. We respect your time and aim to give feedback at every step.
            </p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-7 top-6 bottom-6 w-px hidden md:block"
              style={{ background: `linear-gradient(to bottom, ${O}80, ${B}80)` }} />

            <div className="space-y-5">
              {steps.map((s, i) => (
                <div key={i} className="flex gap-7 items-start group">
                  {/* Number circle */}
                  <div className="relative flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-black text-sm z-10 transition-all duration-300"
                    style={{
                      background: i % 2 === 0 ? `linear-gradient(135deg, ${O}, #e09010)` : `linear-gradient(135deg, ${B}, ${BD})`,
                      color: "white",
                      boxShadow: i % 2 === 0 ? `0 4px 16px ${O}40` : `0 4px 16px ${B}40`,
                    }}>
                    {s.n}
                  </div>

                  <div className="bg-white border border-slate-100 rounded-2xl p-6 flex-1 transition-all duration-200 group-hover:-translate-x-0.5"
                    onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 20px ${B}18`}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                  >
                    <h3 className="font-bold text-slate-800">{s.title}</h3>
                    <p className="mt-1.5 text-slate-500 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── OPEN APPLICATION CTA ──────────────────────────────────────── */}
       <EmailBanner />

      <style>{`
        * { -webkit-font-smoothing: antialiased; }
        html { scroll-behavior: smooth; }
      `}</style>
    </div>
  );
}